import { TopMenuEntry } from './submenu-model';

/**
 * The single source of truth for top-level navigation.
 *
 * Adding a new section? Add an entry here and (if it's a group) include the
 * route prefixes under `matchPrefixes` so deep-linked / refreshed URLs still
 * highlight the correct top-level entry.
 */
export const TOP_MENU_CATALOG: TopMenuEntry[] = [

    // ── Workspace ──────────────────────────────────────────────
    {
        id: 'lead-dashboard',
        label: 'Lead Dashboard',
        icon: 'pi pi-chart-pie',
        group: 'Workspace',
        routerLink: ['/lead-dashboard'],
        matchPrefixes: ['/lead-dashboard'],
        roles: ['ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']
    },
    // Ops Dashboard — moved up to sit right after Lead Dashboard (per request).
    // In Pro mode this is the single-admin's main board, labelled "Leads".
    {
        id: 'operations-dashboard',
        label: 'Ops Dashboard',
        proLabel: 'Leads',
        icon: 'pi pi-chart-bar',
        group: 'Workspace',
        routerLink: ['/operations-dashboard'],
        matchPrefixes: ['/operations-dashboard'],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER']
    },
    // My Work — the same dashboard scoped to the caller (agent: own cases; manager: their department).
    // In Pro mode it reads "Leads".
    {
        id: 'my-work',
        label: 'My Work',
        proLabel: 'Leads',
        icon: 'pi pi-briefcase',
        group: 'Workspace',
        routerLink: ['/my-work'],
        matchPrefixes: ['/my-work'],
        roles: ['ROLE_AGENT', 'ROLE_MANAGER']
    },
    // My Profile moved INTO Settings (see the 'settings' entry below) — it's the first
    // Settings tab and, for agents/managers, the ONLY one they can see.
    // Phase 7 — Find customer + claim-requests now live INSIDE My Work as a
    // tab + popup (see operations-dashboard). Standalone menu entry removed
    // intentionally; the /claim-customer route is still routable as a deep
    // link for back-compat but no longer surfaced in the sidebar.
    // Performance — agent's own activity + outcomes; managers see team totals + a leaderboard.
    {
        id: 'performance',
        label: 'Performance',
        icon: 'pi pi-chart-line',
        group: 'Workspace',
        routerLink: ['/performance'],
        matchPrefixes: ['/performance'],
        roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']
    },
    // Commission — restricted-team overview: leads created + commission earned, drill into each user.
    // Pro has no teams/commission structure, so the entry is hidden there.
    {
        id: 'commission-overview',
        label: 'Commission',
        icon: 'pi pi-percentage',
        group: 'Workspace',
        routerLink: ['/commission-overview'],
        matchPrefixes: ['/commission-overview'],
        roles: ['ROLE_COMPANY_ADMIN'],
        proHidden: true
    },
    // Phase Reviews (maker-checker: managers approve/reject advances parked by phases
    // flagged "Require manager review") is NOT a menu entry any more — it lives inside the
    // Ops Dashboard's Requests tab. The /phase-reviews route itself is kept so deep links
    // still resolve; only the sidebar item was removed (2026-09-10).
    // Deal Forecast — pipeline KPIs, period forecasts (expected close dates), hot/warm/cold
    // triage. Shown in BOTH editions — the money model isn't edition-gated.
    {
        id: 'deal-forecast',
        label: 'Forecast',
        icon: 'pi pi-bullseye',
        group: 'Workspace',
        routerLink: ['/deal-forecast'],
        matchPrefixes: ['/deal-forecast'],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER']
    },
    // Report Builder + scheduled email delivery + Loss Cause AI. Both editions.
    {
        id: 'deal-reports',
        label: 'Reports',
        icon: 'pi pi-chart-bar',
        group: 'Workspace',
        routerLink: ['/deal-reports'],
        matchPrefixes: ['/deal-reports'],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER']
    },
    // Assistant — the AI setup guide (chat + confirmed config changes); also
    // Autopilot team board — coaching lens + mode switch. Managers coach with it;
    // Enterprise surface (Pro gets my-brief + owner briefs instead).
    {
        id: 'autopilot-board',
        label: 'Autopilot',
        icon: 'pi pi-bolt',
        group: 'Workspace',
        routerLink: ['/autopilot-board'],
        matchPrefixes: ['/autopilot-board'],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'],
        proHidden: true
    },
    // reachable everywhere via the floating launcher bottom-right.
    {
        id: 'assistant',
        label: 'Assistant',
        icon: 'pi pi-sparkles',
        group: 'Workspace',
        routerLink: ['/assistant'],
        matchPrefixes: ['/assistant'],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER']
    },
    // Calendar — personal schedule (own events + surfaced tasks). Shown in BOTH Pro and Enterprise.
    {
        id: 'calendar',
        label: 'Calendar',
        icon: 'pi pi-calendar',
        group: 'Workspace',
        routerLink: ['/calendar'],
        matchPrefixes: ['/calendar'],
        roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']
    },
    // Routines — now embedded INSIDE the Calendar page (a "Routine" button in the calendar's
    // left rail swaps the main area to the planner), so the standalone sidebar entry is retired.
    // The /routines route still works by URL (notification deep-links) — uncomment to restore.
    // {
    //     id: 'routines',
    //     label: 'Routines',
    //     icon: 'pi pi-check-circle',
    //     group: 'Workspace',
    //     routerLink: ['/routines'],
    //     matchPrefixes: ['/routines'],
    //     roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']
    // },
    // Active Leads — removed from the menu (the route + page still exist if it's ever wanted back).
    // {
    //     id: 'active-leads',
    //     label: 'Active Leads',
    //     icon: 'pi pi-bolt',
    //     group: 'Workspace',
    //     routerLink: ['/active-leads'],
    //     matchPrefixes: ['/active-leads'],
    //     roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER']
    // },
    // Field Tracking — manager replays a field agent's GPS route for a chosen day.
    {
        id: 'field-tracking',
        label: 'Field Tracking',
        icon: 'pi pi-map-marker',
        group: 'Workspace',
        routerLink: ['/field-tracking'],
        matchPrefixes: ['/field-tracking'],
        roles: ['ROLE_MANAGER', 'ROLE_COMPANY_ADMIN'],
        proHidden: true
    },
    // Attendance — check-in/out + selfies + GPS distance, daily/weekly per agent.
    {
        id: 'attendance',
        label: 'Attendance',
        icon: 'pi pi-clock',
        group: 'Workspace',
        routerLink: ['/attendance'],
        matchPrefixes: ['/attendance'],
        roles: ['ROLE_MANAGER', 'ROLE_COMPANY_ADMIN'],
        proHidden: true
    },
    /* ── Hidden from the left menu (per request). Routes still work by URL;
       uncomment any entry to restore it to the sidebar. ──────────────────
    {
        id: 'leads',
        label: 'Leads',
        icon: 'pi pi-users',
        group: 'Workspace',
        routerLink: ['/leads'],
        matchPrefixes: ['/leads'],
        roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']
    },
    {
        id: 'lead-insights',
        label: 'Lead AI Dashboard',
        icon: 'pi pi-sparkles',
        group: 'Workspace',
        routerLink: ['/lead-insights'],
        matchPrefixes: ['/lead-insights'],
        roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']
    },
    {
        id: 'customer-flows',
        label: 'Customers',
        icon: 'pi pi-id-card',
        group: 'Workspace',
        routerLink: ['/customer-flows'],
        matchPrefixes: ['/customer-flows'],
        roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']
    },
    {
        id: 'support',
        label: 'Support',
        icon: 'pi pi-life-ring',
        group: 'Workspace',
        routerLink: ['/support'],
        matchPrefixes: ['/support'],
        roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']
    },
    ── end hidden block ──────────────────────────────────────────────── */
    {
        id: 'inbox',
        label: 'Inbox',
        icon: 'pi pi-comments',
        group: 'Workspace',
        routerLink: ['/conversations'],
        // '/conversation-details' dropped with its route (retired 2026-09-14) - it only told the
        // sidebar to highlight Conversations while on that URL, which is now unreachable.
        matchPrefixes: ['/conversations'],
        // Agents included: the permission-template checkbox ('inbox' screen key) decides per user;
        // the backend scopes agents to their own assigned conversations.
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER', 'ROLE_AGENT'],
        proHidden: true
    },
    /* ── Hidden from the left menu (per request). Routes still work by URL;
       uncomment to restore. ──────────────────────────────────────────────
    {
        id: 'my-tasks',
        label: 'My Tasks',
        icon: 'pi pi-check-square',
        group: 'Workspace',
        routerLink: ['/my-tasks'],
        matchPrefixes: ['/my-tasks'],
        roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']
    },
    {
        id: 'manager-view',
        label: 'Manager View',
        icon: 'pi pi-chart-bar',
        group: 'Workspace',
        routerLink: ['/manager-view'],
        matchPrefixes: ['/manager-view'],
        roles: ['ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']
    },
    ── end hidden block ──────────────────────────────────────────────── */

    // ── Marketing ──────────────────────────────────────────────
    {
        id: 'marketing',
        label: 'Marketing',
        icon: 'pi pi-megaphone',
        group: 'Marketing',
        matchPrefixes: ['/campaigns'],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'],
        sections: [
            {
                items: [
                    { label: 'All Campaigns',  icon: 'pi pi-megaphone', routerLink: ['/campaigns'] },
                    { label: 'Segments',       icon: 'pi pi-filter',    routerLink: ['/campaigns/segments'] },
                    { label: 'AI Suggestions', icon: 'pi pi-sparkles',  routerLink: ['/campaigns/ai-suggestions'] },
                    { label: 'Analytics',      icon: 'pi pi-chart-bar', routerLink: ['/campaigns/analytics'] },
                    { label: 'Suppression',    icon: 'pi pi-shield',    routerLink: ['/campaigns/suppression'] }
                ]
            }
        ]
    },
    // ── SEO Engine (per-company add-on; super admin enables it in Companies & Wallets) ──
    {
        id: 'seo',
        label: 'SEO Engine',
        icon: 'pi pi-search',
        group: 'Marketing',
        matchPrefixes: ['/seo'],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'],
        sections: [
            {
                items: [
                    { label: 'Dashboard',  icon: 'pi pi-chart-pie',  routerLink: ['/seo/dashboard'] },
                    { label: 'Blog Posts', icon: 'pi pi-file-edit',  routerLink: ['/seo/posts'] },
                    { label: 'Keywords',   icon: 'pi pi-tags',       routerLink: ['/seo/keywords'] },
                    { label: 'Site Audit', icon: 'pi pi-verified',   routerLink: ['/seo/audit'] },
                    { label: 'Fix List',   icon: 'pi pi-wrench',     routerLink: ['/seo/suggestions'] },
                    { label: 'Local Map',  icon: 'pi pi-map',        routerLink: ['/seo/local-map'] },
                    { label: 'AI Visibility', icon: 'pi pi-sparkles', routerLink: ['/seo/ai-visibility'], addon: 'geo' },
                    { label: 'Report',     icon: 'pi pi-chart-line', routerLink: ['/seo/report'] },
                    { label: 'Settings',   icon: 'pi pi-cog',        routerLink: ['/seo/settings'], roles: ['ROLE_COMPANY_ADMIN'] }
                ]
            }
        ]
    },
    // ── Channels ───────────────────────────────────────────────
    // Single top-level "Channels" rail item. The submenu panel groups the
    // four channels (WhatsApp, Email, Chatbot, Website) as labelled sections,
    // each carrying its own pages.
    {
        id: 'channels',
        label: 'Channels',
        icon: 'pi pi-share-alt',
        group: 'Channels',
        matchPrefixes: [
            '/whatsapp-templates',
            '/whatsapp-automation',
            '/lead-wa-configs',
            '/email-templates',
            '/email-config',
            '/email-automation',
            '/email-sequences',
            '/website-builder',
            '/settings?tab=channels'
        ],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'],
        sections: [
            {
                label: 'WhatsApp',
                items: [
                    { label: 'WhatsApp Templates', icon: 'pi pi-whatsapp', routerLink: ['/whatsapp-templates'] },
                    { label: 'WhatsApp Automation', icon: 'pi pi-bolt',    routerLink: ['/whatsapp-automation'] },
                    // Hidden from left menu (per request): { label: 'Lead WA Config', icon: 'pi pi-send', routerLink: ['/lead-wa-configs'] },
                    { label: 'WhatsApp Config',    icon: 'pi pi-cog',      routerLink: ['/settings'], queryParams: { tab: 'channels', channel: 'whatsapp' } }
                ]
            },
            {
                label: 'Email',
                items: [
                    { label: 'Email Templates',  icon: 'pi pi-envelope', routerLink: ['/email-templates'] },
                    // Email Sequences is phase-bound — meaningless in Pro's single journey; use Email Automation there.
                    { label: 'Email Sequences',  icon: 'pi pi-sitemap',  routerLink: ['/email-sequences'], proHidden: true },
                    { label: 'Email Automation', icon: 'pi pi-bolt',     routerLink: ['/email-automation'] },
                    { label: 'Email Config',     icon: 'pi pi-cog',      routerLink: ['/email-config'], roles: ['ROLE_COMPANY_ADMIN'] }
                ]
            },
            {
                label: 'Chatbot',
                items: [
                    { label: 'Chatbot Config', icon: 'pi pi-android', routerLink: ['/settings'], queryParams: { tab: 'channels', channel: 'chatbot' } }
                ]
            },
            {
                label: 'Website',
                items: [
                    { label: 'My Websites', icon: 'pi pi-globe',       routerLink: ['/website-builder'] },
                    { label: 'New Website', icon: 'pi pi-plus-circle', routerLink: ['/website-builder/templates'] }
                ]
            }
        ]
    },
    // ── Analytics ──────────────────────────────────────────────
    {
        id: 'analytics',
        label: 'Analytics',
        icon: 'pi pi-chart-line',
        group: 'Analytics',
        matchPrefixes: ['/journey-analytics', '/dashboard', '/operations-reports'],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'],
        proHidden: true,
        sections: [
            {
                items: [
                    { label: 'Operations Report',   icon: 'pi pi-chart-bar',  routerLink: ['/operations-reports'], roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                    { label: 'Journey Analytics',   icon: 'pi pi-chart-line', routerLink: ['/journey-analytics'],  roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                    { label: 'AI Engine Analytics', icon: 'pi pi-compass',     routerLink: ['/dashboard'],          roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] }
                ]
            }
        ]
    },
    // ── Admin ──────────────────────────────────────────────────
    // The old 'Automation' tab (Workflows / Operations / Lead Sources) was folded into the
    // Settings submenu (per request); its top-level entry was removed.
    {
        id: 'operations',
        label: 'Operations',
        icon: 'pi pi-cog',
        group: 'Admin',
        matchPrefixes: ['/appointments', '/reminders', '/referral-config'],
        roles: ['ROLE_MANAGER', 'ROLE_COMPANY_ADMIN'],
        proHidden: true,
        sections: [
            {
                items: [
                    { label: 'Appointments',    icon: 'pi pi-calendar-clock', routerLink: ['/appointments'] },
                    { label: 'Reminders',       icon: 'pi pi-bell',           routerLink: ['/reminders'],       roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                    { label: 'Referral Config', icon: 'pi pi-share-alt',      routerLink: ['/referral-config'], roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] }
                ]
            }
        ]
    },
    {
        id: 'database',
        label: 'Database',
        icon: 'pi pi-database',
        group: 'Admin',
        matchPrefixes: ['/database', '/vendors', '/customers', '/bulk-uploads', '/lead-import'],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'],
        proHidden: true,
        sections: [
            {
                items: [
                    // 'Catalogs' moved to the Settings submenu (per request).
                    { label: 'Tables',       icon: 'pi pi-table',     routerLink: ['/database/tables'] },
                    { label: 'Vendors',      icon: 'pi pi-briefcase', routerLink: ['/vendors'] },
                    { label: 'Customers',    icon: 'pi pi-users',     routerLink: ['/customers'] },
                    { label: 'Bulk Uploads', icon: 'pi pi-upload',    routerLink: ['/bulk-uploads'] },
                    { label: 'Lead Import',  icon: 'pi pi-file-import', routerLink: ['/lead-import'] }
                ]
            }
        ]
    },
    // ── Logs (monitoring) ──────────────────────────────────────
    {
        id: 'logs',
        label: 'Logs',
        icon: 'pi pi-history',
        group: 'Admin',
        // ONE page now — the /logs hub hosts Bulk Sends, Event Queue and the audit feeds as
        // categorised TABS (per request: "remove submenu and use those as tabs"). The old
        // routes still resolve for bookmarks; the menu just stops advertising them.
        routerLink: ['/logs'],
        matchPrefixes: ['/logs', '/event-queue', '/bulk-send-log'],
        roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER']
    },
    // ── Settings ───────────────────────────────────────────────
    // The flat Settings submenu was RETIRED in favour of the Settings HUB — the card-grid
    // landing page at /settings (see pages/settings/settings-catalog.ts). With no catalog
    // entry matching /settings (or the standalone settings routes: /workflows /operations
    // /catalogs /event-mappings /commission /mobile-app-config /permission-templates), the
    // submenu panel simply closes on those pages and the content runs full-width. Each of
    // those pages carries an <app-settings-backlink> pill back to the hub.

    // ── Super Admin (platform owner only) ──────────────────────────────
    {
        id: 'super-admin',
        label: 'Super Admin',
        icon: 'pi pi-crown',
        group: 'Super Admin',
        matchPrefixes: ['/super-admin'],
        roles: ['ROLE_SUPER_ADMIN'],
        sections: [
            {
                items: [
                    { label: 'Rate Card',           icon: 'pi pi-money-bill', routerLink: ['/super-admin/rate-card'] },
                    { label: 'Companies & Wallets', icon: 'pi pi-wallet',     routerLink: ['/super-admin/wallets'] },
                    { label: 'Data Migration',      icon: 'pi pi-database',   routerLink: ['/super-admin/migration'] },
                    { label: 'Mobile App Update',   icon: 'pi pi-mobile',     routerLink: ['/super-admin/app-version'] },
                    { label: 'Error Logs',          icon: 'pi pi-exclamation-triangle', routerLink: ['/super-admin/error-logs'] }
                ]
            }
        ]
    }
];
